import { onScopeDispose, watch } from 'vue';
import { useWs } from '@/shell/stores/ws';

/**
 * Spread window for the post-reconnect refetch. Reconnects arrive in
 * correlated waves — API Gateway force-closes every socket at its 2-hour
 * maximum, and a venue wifi blip drops a whole room together — and every
 * client that comes back refetches the entity it is watching. Firing those on
 * the `connected` transition puts the whole wave on the origin at once,
 * against a reserved-concurrency ceiling.
 *
 * createWsClient jitters the reconnect itself, but that is not sufficient on
 * its own: connect latency varies enough to re-bunch clients that started out
 * spread, and $connect is slowest exactly when the wave is largest. Two
 * seconds is well inside the window where a bidder reads the page as live,
 * and wide enough to flatten the burst.
 */
const REFETCH_SPREAD_MS = 2000;

export function useReconnectRefresh(fetchFn: () => Promise<void>): void {
  const ws = useWs();
  let connectedOnce = ws.state === 'connected';
  let timer: ReturnType<typeof setTimeout> | null = null;

  // The delay outlives a fast unmount, so a bidder who navigates away during
  // the spread window would otherwise refetch an entity no longer on screen.
  onScopeDispose(() => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  });

  watch(
    () => ws.state,
    status => {
      if (status !== 'connected') return;
      // First connect is the page's own initial load, not a recovery.
      if (!connectedOnce) {
        connectedOnce = true;
        return;
      }
      // A flapping socket can transition to `connected` repeatedly before the
      // pending refetch runs; one refresh settles the state either way.
      if (timer !== null) return;
      timer = setTimeout(
        () => {
          timer = null;
          // Browser code inside a setTimeout, not a Lambda handler — there is
          // no execution environment to freeze and nothing to await it. The
          // catch is the whole error contract: a failed refresh leaves the
          // last-known state on screen rather than tearing the page down.
          // eslint-disable-next-line local-rules/no-floating-side-effect
          fetchFn().catch((err) =>
            console.error('[useReconnectRefresh] fetchFn failed after reconnect', err),
          );
        },
        Math.random() * REFETCH_SPREAD_MS,
      );
    }
  );
}
